import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Flex,
  Text,
  Image,
  VStack,
  HStack,
  Heading,
  Link,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  useToast,
  Icon,
  Spinner,
  Badge,
  Progress,
  Divider,
  Tooltip
} from '@chakra-ui/react';
import { FaTwitter, FaDiscord, FaCheck, FaArrowRight, FaShare, FaExternalLinkAlt, FaInfoCircle } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { useWallet } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import { 
  verifyTwitterEngagement, 
  verifyDiscordRole, 
  storeVerificationStatus, 
  getVerificationStatus,
  isVerificationValid,
  isFullyVerified
} from '../services/verificationService';

const MotionBox = motion(Box);
const MotionFlex = motion(Flex);

const SocialPrompt = ({ onComplete }) => {
  const { isOpen, onOpen, onClose } = useDisclosure({ defaultIsOpen: true });
  const [verificationStatus, setVerificationStatus] = useState({
    twitter: { checked: false, followed: false, shared: false, loading: false },
    discord: { checked: false, joined: false, hasRole: false, loading: false }
  });
  const [overallProgress, setOverallProgress] = useState(0);
  const toast = useToast();
  const { publicKey } = useWallet();
  const { setVisible } = useWalletModal();

  // Check if user is already verified when component mounts
  useEffect(() => {
    const checkExistingVerification = () => {
      const savedStatus = getVerificationStatus();
      
      if (savedStatus && isVerificationValid(savedStatus)) {
        setVerificationStatus({
          twitter: {
            checked: true,
            followed: savedStatus.twitter?.followed || false,
            shared: savedStatus.twitter?.shared || false,
            loading: false
          },
          discord: {
            checked: true,
            joined: savedStatus.discord?.joined || false,
            hasRole: savedStatus.discord?.hasRole || false,
            loading: false
          }
        });
        
        // If fully verified, allow entry
        if (isFullyVerified()) {
          toast({
            title: "Verification Successful",
            description: "Welcome back to Palm Heist!",
            status: "success",
            duration: 3000,
            isClosable: true,
          });
        }
      }
    };

    checkExistingVerification();
  }, [toast]);

  // Update progress bar based on verification status
  useEffect(() => {
    let completedSteps = 0;
    const totalSteps = 4; // Follow Twitter, Share Tweet, Join Discord, Get Role
    
    if (verificationStatus.twitter.followed) completedSteps++;
    if (verificationStatus.twitter.shared) completedSteps++;
    if (verificationStatus.discord.joined) completedSteps++;
    if (verificationStatus.discord.hasRole) completedSteps++;
    
    setOverallProgress((completedSteps / totalSteps) * 100);
  }, [verificationStatus]);

  const handleVerifyTwitter = async () => {
    // Mock verification for demonstration purposes
    setVerificationStatus(prev => ({
      ...prev,
      twitter: { ...prev.twitter, loading: true }
    }));
    
    try {
      // Simulate successful verification
      setTimeout(() => {
        setVerificationStatus(prev => ({
          ...prev,
          twitter: { 
            checked: true,
            followed: true,
            shared: true,
            loading: false
          }
        }));
        
        toast({
          title: "Twitter Verification Complete",
          description: "Successfully verified Twitter engagement",
          status: "success",
          duration: 4000,
          isClosable: true,
        });
      }, 1500);
    } catch (error) {
      console.error("Error during Twitter verification:", error);
      toast({
        title: "Verification Error",
        description: "An unexpected error occurred. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      
      setVerificationStatus(prev => ({
        ...prev,
        twitter: { ...prev.twitter, loading: false }
      }));
    }
  };

  const handleVerifyDiscord = async () => {
    // Mock verification for demonstration purposes
    setVerificationStatus(prev => ({
      ...prev,
      discord: { ...prev.discord, loading: true }
    }));
    
    try {
      // Simulate successful verification
      setTimeout(() => {
        setVerificationStatus(prev => ({
          ...prev,
          discord: { 
            checked: true,
            joined: true,
            hasRole: true,
            loading: false
          }
        }));
        
        toast({
          title: "Discord Verification Complete",
          description: "Successfully verified Discord membership",
          status: "success",
          duration: 4000,
          isClosable: true,
        });
      }, 1500);
    } catch (error) {
      console.error("Error during Discord verification:", error);
      toast({
        title: "Verification Error",
        description: "An unexpected error occurred. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      
      setVerificationStatus(prev => ({
        ...prev,
        discord: { ...prev.discord, loading: false }
      }));
    }
  };

  // For testing purposes, let's auto-verify both Twitter and Discord
  useEffect(() => {
    // Auto-verify after a short delay for testing
    const timer = setTimeout(() => {
      setVerificationStatus({
        twitter: { 
          checked: true,
          followed: true,
          shared: true,
          loading: false
        },
        discord: { 
          checked: true,
          joined: true,
          hasRole: true,
          loading: false
        }
      });
    }, 500);
    
    return () => clearTimeout(timer);
  }, []);

  const handleComplete = () => {
    const { twitter, discord } = verificationStatus;
    const isComplete = twitter.followed && twitter.shared && discord.joined && discord.hasRole;
    
    if (isComplete) {
      // Make sure to call onComplete before closing the modal
      if (onComplete) onComplete();
      
      // Close the modal after a short delay
      setTimeout(() => {
        onClose();
        
        toast({
          title: "Welcome to Palm Heist!",
          description: "Thanks for joining our community. Enjoy the game!",
          status: "success",
          duration: 5000,
          isClosable: true,
          position: "top",
        });
      }, 300);
    } else {
      toast({
        title: "Verification Required",
        description: "Please complete all verification steps to continue",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const isAllVerified = () => {
    const { twitter, discord } = verificationStatus;
    return twitter.followed && twitter.shared && discord.joined && discord.hasRole;
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      size="xl" 
      isCentered 
      closeOnOverlayClick={false}
      motionPreset="slideInBottom"
    >
      <ModalOverlay 
        bg="blackAlpha.800"
        backdropFilter="blur(10px)"
      />
      <ModalContent 
        bg="linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)"
        borderRadius="xl"
        border="1px solid"
        borderColor="rgba(255,255,255,0.1)"
        boxShadow="0 0 40px rgba(0, 230, 118, 0.3)"
        overflow="hidden"
        p={0}
        position="relative"
        maxH="90vh"
        h="auto"
      >
        <ModalHeader p={0} position="relative">
          <Flex 
            bg="linear-gradient(90deg, #00E676 0%, #00B0FF 100%)" 
            p={4}
            align="center"
            position="relative"
            overflow="hidden"
            justifyContent="space-between"
          >
            <Flex align="center">
              <Image 
                src="/logo.svg" 
                alt="Palm Heist Logo" 
                h="50px" 
                mr={4}
                zIndex={1}
                fallbackSrc="https://via.placeholder.com/50x50?text=PH"
              />
              <Heading color="white" fontSize="2xl" fontWeight="bold" zIndex={1}>
                Enter the Treasure Realm!
              </Heading>
            </Flex>
            <Box position="relative" top="-8px" right="-8px">
              <ModalCloseButton 
                color="white" 
                zIndex={10} 
                position="static" 
                bg="rgba(0,0,0,0.3)"
                borderRadius="full"
                size="md"
                _hover={{
                  bg: "rgba(0,0,0,0.5)",
                  color: "#FFD700"
                }}
              />
            </Box>
            <MotionBox
              position="absolute"
              top="-50%"
              left="-10%"
              width="120%"
              height="300%"
              bg="linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0) 100%)"
              initial={{ x: -500 }}
              animate={{ 
                x: 500,
                transition: { 
                  repeat: Infinity, 
                  duration: 2,
                  ease: "linear"
                }
              }}
              style={{ transform: "rotate(30deg)" }}
            />
          </Flex>
        </ModalHeader>
        <ModalBody py={6} px={{ base: 4, md: 6 }} overflowY="auto" maxH="calc(90vh - 120px)">
          <VStack spacing={6} align="stretch">
            <Text fontSize="lg" color="white" textAlign="center">
              One click away, the time is right—join the heist and claim your bite!
            </Text>
            
            {/* Progress bar */}
            <Box>
              <HStack justify="space-between" mb={1}>
                <Text color="white" fontSize="sm">Verification Progress</Text>
                <Text color="white" fontSize="sm">{Math.round(overallProgress)}%</Text>
              </HStack>
              <Progress 
                value={overallProgress} 
                size="sm" 
                colorScheme="green" 
                borderRadius="full"
                bg="whiteAlpha.200"
              />
            </Box>
            
            {/* Social Verification Sections - Stack vertically */}
            <VStack spacing={4} align="stretch">
              {/* Twitter Section */}
              <MotionFlex
                direction="column"
                bg="rgba(0,0,0,0.3)"
                borderRadius="lg"
                p={4}
                border="1px solid"
                borderColor="rgba(29, 161, 242, 0.3)"
                whileHover={{ 
                  boxShadow: "0 0 15px rgba(29, 161, 242, 0.5)",
                  borderColor: "rgba(29, 161, 242, 0.7)",
                  y: -2
                }}
                transition={{ duration: 0.2 }}
              >
                <HStack spacing={4} mb={4}>
                  <Icon as={FaTwitter} color="#1DA1F2" w={8} h={8} />
                  <VStack align="start" spacing={0}>
                    <Heading size="md" color="white">Follow on Twitter</Heading>
                    <Text color="whiteAlpha.700" fontSize="sm">@palmheist</Text>
                  </VStack>
                </HStack>
                
                <VStack spacing={4} align="stretch">
                  {/* Follow Status */}
                  <HStack justify="space-between" bg="whiteAlpha.50" p={3} borderRadius="md">
                    <HStack>
                      <Icon 
                        as={verificationStatus.twitter.followed ? FaCheck : FaTwitter} 
                        color={verificationStatus.twitter.followed ? "green.400" : "gray.400"} 
                        mr={2}
                      />
                      <Text color="white">Follow @palmheist</Text>
                    </HStack>
                    
                    {verificationStatus.twitter.followed ? (
                      <Badge colorScheme="green">Verified</Badge>
                    ) : (
                      <Link 
                        href="https://x.com/palmheist" 
                        isExternal
                        _hover={{ textDecoration: 'none' }}
                      >
                        <Button 
                          rightIcon={<FaExternalLinkAlt />} 
                          colorScheme="twitter" 
                          size="xs"
                          variant="outline"
                          borderRadius="full"
                        >
                          Follow
                        </Button>
                      </Link>
                    )}
                  </HStack>
                  
                  {/* Share Status */}
                  <HStack justify="space-between" bg="whiteAlpha.50" p={3} borderRadius="md">
                    <HStack>
                      <Icon 
                        as={verificationStatus.twitter.shared ? FaCheck : FaShare} 
                        color={verificationStatus.twitter.shared ? "green.400" : "gray.400"} 
                        mr={2}
                      />
                      <Text color="white">Share our latest post</Text>
                    </HStack>
                    
                    {verificationStatus.twitter.shared ? (
                      <Badge colorScheme="green">Verified</Badge>
                    ) : (
                      <Link 
                        href="https://x.com/palmheist/status/1234567890" 
                        isExternal
                        _hover={{ textDecoration: 'none' }}
                      >
                        <Button 
                          rightIcon={<FaShare />} 
                          colorScheme="twitter" 
                          size="xs"
                          variant="outline"
                          borderRadius="full"
                        >
                          Share
                        </Button>
                      </Link>
                    )}
                  </HStack>
                  
                  <Button
                    leftIcon={verificationStatus.twitter.loading ? <Spinner size="sm" /> : <FaCheck />}
                    colorScheme={verificationStatus.twitter.followed && verificationStatus.twitter.shared ? "green" : "twitter"}
                    onClick={handleVerifyTwitter}
                    isLoading={verificationStatus.twitter.loading}
                    loadingText="Verifying..."
                    size="sm"
                    mt={2}
                    variant="outline"
                    borderRadius="full"
                  >
                    {verificationStatus.twitter.checked 
                      ? (verificationStatus.twitter.followed && verificationStatus.twitter.shared 
                        ? "Verified" 
                        : "Verify Again") 
                      : "Verify Twitter Actions"}
                  </Button>
                </VStack>
              </MotionFlex>
              
              {/* Discord Section */}
              <MotionFlex
                direction="column"
                bg="rgba(0,0,0,0.3)"
                borderRadius="lg"
                p={4}
                border="1px solid"
                borderColor="rgba(114, 137, 218, 0.3)"
                whileHover={{ 
                  boxShadow: "0 0 15px rgba(114, 137, 218, 0.5)",
                  borderColor: "rgba(114, 137, 218, 0.7)",
                  y: -2
                }}
                transition={{ duration: 0.2 }}
              >
                <HStack spacing={4} mb={4}>
                  <Icon as={FaDiscord} color="#7289DA" w={8} h={8} />
                  <VStack align="start" spacing={0}>
                    <Heading size="md" color="white">Join our Discord</Heading>
                    <Text color="whiteAlpha.700" fontSize="sm">Get the Heister Role</Text>
                  </VStack>
                </HStack>
                
                <VStack spacing={4} align="stretch">
                  {/* Join Status */}
                  <HStack justify="space-between" bg="whiteAlpha.50" p={3} borderRadius="md">
                    <HStack>
                      <Icon 
                        as={verificationStatus.discord.joined ? FaCheck : FaDiscord} 
                        color={verificationStatus.discord.joined ? "green.400" : "gray.400"} 
                        mr={2}
                      />
                      <Text color="white">Join Discord Server</Text>
                    </HStack>
                    
                    {verificationStatus.discord.joined ? (
                      <Badge colorScheme="green">Verified</Badge>
                    ) : (
                      <Link 
                        href="https://discord.gg/8GnWVtKth6" 
                        isExternal
                        _hover={{ textDecoration: 'none' }}
                      >
                        <Button 
                          rightIcon={<FaExternalLinkAlt />} 
                          colorScheme="purple" 
                          size="xs"
                          variant="outline"
                          borderRadius="full"
                        >
                          Join
                        </Button>
                      </Link>
                    )}
                  </HStack>
                  
                  {/* Role Status */}
                  <HStack justify="space-between" bg="whiteAlpha.50" p={3} borderRadius="md">
                    <HStack>
                      <Icon 
                        as={verificationStatus.discord.hasRole ? FaCheck : FaInfoCircle} 
                        color={verificationStatus.discord.hasRole ? "green.400" : "gray.400"} 
                        mr={2}
                      />
                      <Text color="white">Get Heister Role</Text>
                    </HStack>
                    
                    {verificationStatus.discord.hasRole ? (
                      <Badge colorScheme="green">Verified</Badge>
                    ) : (
                      <Tooltip label="Go to #roles channel and click on the Heister emoji" placement="top">
                        <Link 
                          href="https://discord.gg/8GnWVtKth6" 
                          isExternal
                          _hover={{ textDecoration: 'none' }}
                        >
                          <Button 
                            rightIcon={<FaInfoCircle />} 
                            colorScheme="purple" 
                            size="xs"
                            variant="outline"
                            borderRadius="full"
                          >
                            How to
                          </Button>
                        </Link>
                      </Tooltip>
                    )}
                  </HStack>
                  
                  <Button
                    leftIcon={verificationStatus.discord.loading ? <Spinner size="sm" /> : <FaCheck />}
                    colorScheme={verificationStatus.discord.joined && verificationStatus.discord.hasRole ? "green" : "discord"}
                    onClick={handleVerifyDiscord}
                    isLoading={verificationStatus.discord.loading}
                    loadingText="Verifying..."
                    size="sm"
                    mt={2}
                    variant="outline"
                    borderRadius="full"
                  >
                    {verificationStatus.discord.checked 
                      ? (verificationStatus.discord.joined && verificationStatus.discord.hasRole 
                        ? "Verified" 
                        : "Verify Again") 
                      : "Verify Discord Status"}
                  </Button>
                </VStack>
              </MotionFlex>
            </VStack>
            
            <Divider borderColor="whiteAlpha.300" />
            
            <Button
              onClick={handleComplete}
              size="lg"
              colorScheme="green"
              bg={isAllVerified() ? "linear-gradient(90deg, #00E676 0%, #00C853 100%)" : "transparent"}
              _hover={{
                bg: isAllVerified() ? "linear-gradient(90deg, #00C853 0%, #00B04F 100%)" : "transparent",
                borderColor: "#00E676",
                boxShadow: "0 0 10px rgba(0, 230, 118, 0.5)"
              }}
              rightIcon={isAllVerified() ? <FaCheck /> : <FaArrowRight />}
              isDisabled={!isAllVerified()}
              borderRadius="full"
              variant={isAllVerified() ? "solid" : "outline"}
              borderColor={isAllVerified() ? "transparent" : "#00E676"}
              borderWidth="2px"
            >
              {isAllVerified() ? "Enter Palm Heist" : "Complete All Steps to Continue"}
            </Button>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default SocialPrompt;
